import React from "react";
import {
  Button,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { importTrainingPlan } from "../../../../data/TrainingPlanImportData";
import {
  TRAINING_PLAN_IMPORT_SOURCES,
  TRAINING_PLAN_IMPORT_SOURCE_STORAGE_KEY,
  loadStoredTrainingPlanImportSource,
  normalizeTrainingPlanImportSource,
} from "../../../../data/TrainingPlanImportSources";
import useEntitlements from "../../../../hooks/useEntitlements";
import UpgradePrompt from "../../../../components/organisms/UpgradePrompt";
import { trackTrainingPlansV2Event } from "./services/trainingPlansV2Service";

const SCOPES = ["template", "client"];

function normalizeScope(value) {
  const v = String(value || "").trim();
  return SCOPES.includes(v) ? v : "template";
}

export default function TrainingPlansV2LibraryImportToolbar({ onImportError }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const importInputRef = React.useRef(null);
  const [importingPlan, setImportingPlan] = React.useState(false);
  const [importSource, setImportSource] = React.useState(loadStoredTrainingPlanImportSource);
  const { hasFeature, organizationData } = useEntitlements();
  const canImportPlans = hasFeature("training_plan_imports");

  const scope = normalizeScope(searchParams.get("scope"));
  const selectedClientUID = scope === "client" ? searchParams.get("clientUid") || "" : "";

  const emitImportError = React.useCallback(
    (message) => {
      if (typeof onImportError === "function") {
        onImportError(message || "");
      }
    },
    [onImportError],
  );

  const handleImportFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    emitImportError("");
    setImportingPlan(true);
    try {
      const formData = new FormData();
      formData.append("file", file, file.name || "training-plan.pdf");
      formData.append("importSource", normalizeTrainingPlanImportSource(importSource));
      if (scope === "client" && selectedClientUID) {
        formData.append("clientUID", selectedClientUID);
      }
      const result = await importTrainingPlan(formData);
      await trackTrainingPlansV2Event("plan_ai_import", {
        protocolId: result.protocolId,
        clientUid: scope === "client" ? selectedClientUID || null : null,
      });
      navigate(`/home/training-plans-v2/${result.protocolId}/edit${location.search}`);
    } catch (err) {
      emitImportError(err?.message || "Import failed. Please try again.");
    } finally {
      setImportingPlan(false);
      e.target.value = "";
    }
  };

  if (!canImportPlans) {
    return (
      <UpgradePrompt
        organizationData={organizationData}
        minTier="pro"
        variant="banner"
        title="Upgrade to import training plans"
        description="PDF and image import is available on Pro."
        featureName="training_plan_imports"
      />
    );
  }

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent={{ xs: "stretch", sm: "flex-end" }}
      sx={{
        flex: "0 0 auto",
        ml: { xs: 0, sm: "auto" },
        width: { xs: "100%", sm: "auto" },
      }}
      useFlexGap
    >
      <input
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
        style={{ display: "none" }}
        ref={importInputRef}
        onChange={handleImportFileChange}
      />
      <FormControl
        size="small"
        sx={{
          minWidth: { xs: "100%", sm: 200 },
          maxWidth: { xs: "100%", sm: 280 },
          "& .MuiOutlinedInput-root": {
            height: 36,
            minHeight: 36,
            boxSizing: "border-box",
          },
        }}
        disabled={importingPlan}
      >
        <InputLabel id="tp-v2-library-import-format-label">Import format</InputLabel>
        <Select
          labelId="tp-v2-library-import-format-label"
          label="Import format"
          value={normalizeTrainingPlanImportSource(importSource)}
          onChange={(e) => {
            const v = e.target.value;
            setImportSource(v);
            try {
              window.localStorage.setItem(TRAINING_PLAN_IMPORT_SOURCE_STORAGE_KEY, v);
            } catch (_) {
              /* ignore */
            }
          }}
          renderValue={(v) => {
            const o = TRAINING_PLAN_IMPORT_SOURCES.find((x) => x.id === v);
            return (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <TuneIcon sx={{ fontSize: 18, opacity: 0.85 }} />
                <span>{o?.label ?? v}</span>
              </Stack>
            );
          }}
        >
          {TRAINING_PLAN_IMPORT_SOURCES.map((opt) => (
            <MenuItem key={opt.id} value={opt.id}>
              <ListItemText primary={opt.label} secondary={opt.description} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        size="small"
        variant="outlined"
        startIcon={<UploadFileIcon fontSize="small" />}
        disabled={importingPlan}
        onClick={() => importInputRef.current?.click()}
        sx={{
          alignSelf: { xs: "stretch", sm: "auto" },
          whiteSpace: "nowrap",
          height: 36,
          minHeight: 36,
          boxSizing: "border-box",
          py: 0,
        }}
      >
        {importingPlan ? "Analyzing…" : "Import PDF / image"}
      </Button>
    </Stack>
  );
}
